import React, { useState, useEffect } from "react";
import { RRule } from "rrule";
import makeStyles from "@mui/styles/makeStyles";
import {
    Autocomplete,
    Paper,
    FormControl,
    Grid,
    Typography,
    InputLabel,
    Divider,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    FormHelperText,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Button,
} from "@mui/material";
// import MultiSelect from "react-multi-select-component";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { format } from "react-string-format";
import GenericDatePicker from "../GenericDatePicker/GenericDatePicker";
import { useLocation } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    popupElement: {
        width: "40%",
        marginLeft: 20,
    },
    label: {
        margin: 20,
        alignSelf: "center",
    },
    section: {
        marginTop: 20,
        marginBottom: 20,
    },
    formControl: {
        margin: theme.spacing(1),
    },
    formText: {
        top: "30%",
    },
    textContainer: {
        display: "flex",
    },
}));

const RuleGenerator = (props) => {
    const classes = useStyles();
    const { t } = useTranslation("translation");

    const days = Array.from({ length: 31 }, (_, id) => ({ label: (id + 1).toString(), value: id + 1 }));
    const weekDays = [
        { id: RRule.MO, name: t("Monday") },
        { id: RRule.TU, name: t("Tuesday") },
        { id: RRule.WE, name: t("Wednesday") },
        { id: RRule.TH, name: t("Thursday") },
        { id: RRule.FR, name: t("Friday") },
        { id: RRule.SA, name: t("Saturday") },
        { id: RRule.SU, name: t("Sunday") },
    ];
    const weekDayOptions = [
        { id: 1, name: t("First") },
        { id: 2, name: t("Second") },
        { id: 3, name: t("Third") },
        { id: 4, name: t("Fourth") },
        { id: -1, name: t("Last") },
    ];

    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [recurrenceType, setRecurrenceType] = useState(props.ruleString === "" ? RRule.DAILY : RRule.MONTHLY);
    const [endType, setEndType] = useState("byCount");
    const [counts, setCounts] = useState(1);
    const [mountlyFreq, setMountlyFreq] = useState(1);
    const [weeklyFreq, setWeeklyFreq] = useState(1);
    const [dailyFreq, setDailyFreq] = useState(1);
    const [onTheDayOfMonths, setOnTheDayOfMonths] = useState([]);
    const [weekDayOptionOfMonth, setWeekDayOptionOfMonth] = useState(1);
    const [selectedDayOfMonth, setSelectedDayOfMonth] = useState(RRule.MO);
    const [chxMonthByDay, setChxMonthByDay] = useState(true);
    const [selectedWeekDays, setSelectedWeekDays] = useState([]);
    const location = useLocation();

    useEffect(() => {
        initialize();
    }, [props.rule]);

    const initialize = () => {
        if (props.rule === null) {
            return;
        }

        props.rule.dtstart.getTime() > new Date().getTime() ? setStartDate(props.rule.dtstart) : setStartDate(new Date());

        setRecurrenceType(props.rule.freq);
        setEndDate(props.rule.until);
        setEndType(props.rule.until ? "byDate" : "byCount");
        setChxMonthByDay(props.rule.bysetpos ? false : true);
        setCounts(props.rule.count);
        setMountlyFreq(props.rule.freq === RRule.MONTHLY ? props.rule.interval : 1);
        setWeeklyFreq(props.rule.freq === RRule.WEEKLY ? props.rule.interval : 2);
        setDailyFreq(props.rule.freq === RRule.DAILY ? props.rule.interval : 3);

        let days = [];
        if (props.rule && props.rule.bymonthday) {
            try {
                days = props.rule.bymonthday.map((item) => {
                    return { label: item, value: item };
                });
            } catch (error) {
                days.push({ label: props.rule.bymonthday, value: props.rule.bymonthday });
            }
        }

        setOnTheDayOfMonths(days);
        setWeekDayOptionOfMonth(props.rule.bysetpos);
        let weekDay = null;
        if (props.rule && props.rule.byweekday && props.rule.bysetpos) {
            weekDay = weekDays[props.rule.byweekday[0].weekday].id;
        }
        setSelectedDayOfMonth(weekDay);
        setSelectedWeekDays(props.rule.byweekday);
    };

    const createRule = () => {
        const dtstart = startDate;
        const freq = recurrenceType;
        let interval = 1;
        let count = 365;
        let until = null;
        let bysetpos = [];
        let byweekday = [];
        let bymonthday = [];

        if (freq === RRule.MONTHLY) {
            interval = mountlyFreq;

            if (chxMonthByDay) {
                bymonthday = onTheDayOfMonths.map((item) => item.value);
            } else {
                bysetpos = weekDayOptionOfMonth;
                byweekday = selectedDayOfMonth;
            }
        } else if (freq === RRule.WEEKLY) {
            interval = weeklyFreq;
            byweekday = selectedWeekDays;
        } else {
            interval = dailyFreq;
        }

        if (endType === "byCount") {
            count = counts;
        } else {
            until = endDate;
        }

        let untilDate = null;
        if (until) {
            untilDate = new Date(until.getFullYear(), until.getMonth(), until.getDate(), until.getHours(), until.getMinutes());
        }
        const rruleConfig = {
            freq: freq,
            byweekday: byweekday,
            bysetpos: bysetpos,
            bymonthday: bymonthday,
            dtstart: new Date(dtstart.getFullYear(), dtstart.getMonth(), dtstart.getDate(), dtstart.getHours(), dtstart.getMinutes()),
            until: untilDate,
            count: count,
            interval: parseInt(interval),
        };

        return new RRule(rruleConfig);
    };

    const submit = () => {
        const rule = createRule();
        let rules = [];
        rule.all().forEach((element) => {
            rules.push(element.toISOString());
        });
        props.onSubmit(rules, rule.toString(), rule.toText());
    };

    const showDays = () => {
        const rule = createRule();
        let willBeShowedDates = [];
        rule.all().forEach((element) => {
            willBeShowedDates.push(moment(element).utcOffset("GMT +3:00").format("DD.MM.YYYY"));
        });

        alert(willBeShowedDates.join("\n"));
    };

    const onStartDateChangeHandler = (date) => {
        setStartDate(date);
    };

    const onEndDateChangeHandler = (date) => {
        setEndDate(date);
    };

    const selectedWeekDaysChangedHandler = (selected, id) => {
        if (selected) {
            setSelectedWeekDays((prevState) => {
                if (prevState == undefined || prevState.length === 0) {
                    prevState = [];
                    prevState.push(id);
                } else {
                    prevState = prevState.concat(id);
                }

                return prevState;
            });
        } else {
            setSelectedWeekDays((prevState) => prevState.filter((x) => x !== id));
        }
    };

    const setCountsHandler = (event) => {
        if (event.target.value > 100) {
            setCounts(100);
            return;
        }

        if (event.target.value <= 0) {
            setCounts("");
        } else {
            setCounts(event.target.value);
        }
    };

    const setMountlyFreqHandler = (event) => {
        if (event.target.value <= 0) {
            setMountlyFreq("");
        } else {
            setMountlyFreq(event.target.value);
        }
    };

    const setWeeklyFreqHandler = (event) => {
        if (event.target.value <= 0) {
            setWeeklyFreq("");
        } else {
            setWeeklyFreq(event.target.value);
        }
    };

    const setDailyFreqHandler = (event) => {
        if (event.target.value <= 0) {
            setDailyFreq("");
        } else {
            setDailyFreq(event.target.value);
        }
    };

    //override strings in multiselect
    const stringTR = {
        selectSomeItems: t("SelectSomeItems"),
        allItemsAreSelected: t("AllItemsAreSelected"),
        selectAll: t("SelectAll"),
        search: t("Search"),
    };

    const EmptyRow = () => {
        return (
            <Grid item xs={12} style={{ margin: 25 }}>
                <Grid item xs>
                    <Divider />
                </Grid>
            </Grid>
        );
    };

    const renderSelect = (menuItem, label, value, onSelecChange) => {
        return (
            <Select
                labelId={`demo-simple-select-outlined-label-${label}`}
                id={`demo-simple-select-outlined-${label}`}
                value={value}
                onChange={(event) => onSelecChange(event.target.value)}
                label={label}
                autoWidth
            >
                {menuItem()}
            </Select>
        );
    };

    const [monthDays, setMonthDays] = useState([]);

    return (
        <div className={classes.root}>
            <Paper elevation={3} style={{ width: "100%" }}>
                <form>
                    <Grid container>
                        {/* Başlangıç zamanı */}
                        <Grid item xs={3}>
                            <FormControl className={classes.label}>
                                <Typography variant="h6" component="h6">
                                    {t("Start")}
                                </Typography>
                            </FormControl>
                        </Grid>
                        <Grid item xs={9}>
                            <FormControl variant="outlined" className={classes.popupElement}>
                                <GenericDatePicker dateValue={startDate} onDateChange={onStartDateChangeHandler} label={t("StartDate")} viewDays={true}/>
                            </FormControl>
                        </Grid>
                        <EmptyRow />

                        {/* Tekrar tipi */}
                        <Grid item xs={3}>
                            <FormControl className={classes.label}>
                                <Typography variant="h6" component="h6">
                                    {t("Repeat")}
                                </Typography>
                            </FormControl>
                        </Grid>
                        <Grid item xs={9}>
                            <Grid container item direction="row">
                                {recurrenceType === RRule.MONTHLY ? <Grid item xs={1}></Grid> : null}
                                <Grid item xs={3}>
                                    <FormControl variant="outlined" className={classes.formControl}>
                                        <InputLabel id="demo-simple-select-outlined-label">{t("Repeat")}</InputLabel>
                                        {renderSelect(
                                            () =>
                                                [
                                                    { val: RRule.MONTHLY, name: t("Monthly") },
                                                    { val: RRule.WEEKLY, name: t("Weekly") },
                                                    { val: RRule.DAILY, name: t("Daily") },
                                                ].map((item, index) => {
                                                    return (
                                                        <MenuItem key={index} value={item.val}>
                                                            {item.name}
                                                        </MenuItem>
                                                    );
                                                }),
                                            t("Repeat"),
                                            recurrenceType,
                                            setRecurrenceType
                                        )}
                                    </FormControl>
                                </Grid>
                                {/* aylık frekans */}
                                {recurrenceType === RRule.MONTHLY ? (
                                    <Grid item xs={5}>
                                        <FormControl className={classes.formControl} variant="outlined" style={{ width: "70%" }}>
                                            <TextField
                                                type="number"
                                                value={mountlyFreq}
                                                onChange={setMountlyFreqHandler}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">{t("OnceAMonth")}</InputAdornment>,
                                                    inputProps: { min: 1 },
                                                }}
                                                variant="outlined"
                                            />
                                        </FormControl>
                                    </Grid>
                                ) : null}

                                {recurrenceType === RRule.WEEKLY ? (
                                    <Grid item xs={5}>
                                        <FormControl className={classes.formControl} variant="outlined" style={{ width: "75%" }}>
                                            <TextField
                                                type="number"
                                                value={weeklyFreq}
                                                onChange={setWeeklyFreqHandler}
                                                InputProps={{
                                                    inputProps: { min: 1 },
                                                    endAdornment: <InputAdornment position="end">{t("OnceAWeek")}</InputAdornment>,
                                                }}
                                                variant="outlined"
                                            />
                                        </FormControl>
                                    </Grid>
                                ) : null}

                                {recurrenceType === RRule.DAILY ? (
                                    <Grid item xs={5}>
                                        <FormControl className={classes.formControl} variant="outlined" style={{ width: "70%" }}>
                                            <TextField
                                                type="number"
                                                value={dailyFreq}
                                                onChange={setDailyFreqHandler}
                                                InputProps={{
                                                    inputProps: { min: 1 },
                                                    endAdornment: <InputAdornment position="end">{t("OnceADay")}</InputAdornment>,
                                                }}
                                                variant="outlined"
                                            />
                                        </FormControl>
                                    </Grid>
                                ) : null}
                            </Grid>

                            {recurrenceType !== RRule.DAILY ? <EmptyRow /> : null}

                            {recurrenceType === RRule.MONTHLY ? (
                                <React.Fragment>
                                    <Grid item xs={12}>
                                        <Grid container item direction="row">
                                            <Grid item xs={1}>
                                                <FormControl className={classes.formControl}>
                                                    <Checkbox
                                                        checked={chxMonthByDay}
                                                        onChange={(event) => setChxMonthByDay(event.target.checked)}
                                                        color="primary"
                                                        inputProps={{ "aria-label": "secondary checkbox" }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <div style={{ marginBottom: 10, marginLeft: 10 }}>
                                                    {/* <Grid item>
                                                        <MultiSelect
                                                            className={dark}
                                                            disabled={!chxMonthByDay}
                                                            overrideStrings={stringTR}
                                                            value={onTheDayOfMonths}
                                                            onChange={setOnTheDayOfMonths}
                                                            options={days}
                                                            labelledBy={"Select"}
                                                        />
                                                    </Grid> */}
                                                    <FormControl variant="outlined" fullWidth>
                                                        <Autocomplete
                                                            multiple
                                                            options={days}
                                                            limitTags={5}
                                                            value={days.filter((item) => monthDays.includes(item.value))}
                                                            disableCloseOnSelect
                                                            getOptionLabel={(option) => option.label}
                                                            renderInput={(params) => <TextField {...params} margin="normal" label={t("SelectSomeItems")} variant="outlined" />}
                                                            onChange={(_event, array) => {
                                                                setOnTheDayOfMonths(array);
                                                                let values = [];
                                                                array.forEach((item) => {
                                                                    values.push(item.value);
                                                                });
                                                                setMonthDays(values);
                                                            }}
                                                            renderOption={(_props, option) => <li {..._props}>{option.label}</li>}
                                                            size="small"
                                                        />
                                                    </FormControl>

                                                    {onTheDayOfMonths.length > 0 ? (
                                                        <FormHelperText>
                                                            {format(
                                                                t("SelectedDaysOfMonth"),
                                                                onTheDayOfMonths.map((item) => item.label + ". ")
                                                            )}
                                                        </FormHelperText>
                                                    ) : null}
                                                </div>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid container item direction="row">
                                        <Grid item xs={1}>
                                            <FormControl className={classes.formControl}>
                                                <Checkbox
                                                    checked={!chxMonthByDay}
                                                    onChange={(event) => setChxMonthByDay(!event.target.checked)}
                                                    color="primary"
                                                    inputProps={{ "aria-label": "secondary checkbox" }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <FormControl disabled={chxMonthByDay} variant="outlined" className={classes.formControl}>
                                                {renderSelect(
                                                    () =>
                                                        weekDayOptions.map((item) => {
                                                            return <MenuItem value={item.id}>{item.name}</MenuItem>;
                                                        }),
                                                    "",
                                                    weekDayOptionOfMonth,
                                                    setWeekDayOptionOfMonth
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControl disabled={chxMonthByDay} className={classes.formControl} variant="outlined" style={{ width: "50%" }}>
                                                {renderSelect(
                                                    () =>
                                                        weekDays.map((item) => {
                                                            return <MenuItem value={item.id}>{item.name}</MenuItem>;
                                                        }),
                                                    "",
                                                    selectedDayOfMonth,
                                                    setSelectedDayOfMonth
                                                )}
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </React.Fragment>
                            ) : null}

                            {recurrenceType === RRule.WEEKLY ? (
                                <FormGroup row>
                                    {weekDays.map((item) => {
                                        return (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        onChange={(event) => selectedWeekDaysChangedHandler(event.target.checked, item.id)}
                                                        checked={selectedWeekDays ? selectedWeekDays.includes(item.id) : null}
                                                        name={item.name}
                                                    />
                                                }
                                                label={item.name}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            ) : null}
                        </Grid>
                        <EmptyRow />

                        {/* Bitiş zamanı */}
                        <Grid item xs={3}>
                            <FormControl className={classes.label}>
                                <Typography variant="h6" component="h6">
                                    {t("Complete")}
                                </Typography>
                            </FormControl>
                        </Grid>
                        <Grid item xs={9}>
                            <FormControl variant="outlined" className={classes.formControl}>
                                <InputLabel>{t("End")}</InputLabel>
                                {renderSelect(
                                    () =>
                                        [
                                            { id: "byCount", name: t("MaintenanceNumber") },
                                            { id: "byDate", name: t("TimeBased") },
                                        ].map((item) => {
                                            return <MenuItem value={item.id}>{item.name}</MenuItem>;
                                        }),
                                    t("Repeat"),
                                    endType,
                                    setEndType
                                )}
                            </FormControl>

                            {endType === "byCount" ? (
                                <FormControl variant="outlined" className={classes.formControl} style={{ width: "30%" }}>
                                    <TextField
                                        type="number"
                                        onChange={setCountsHandler}
                                        value={counts}
                                        InputProps={{
                                            inputProps: { min: 1 },
                                            endAdornment: <InputAdornment position="end">{t("Piece")}</InputAdornment>,
                                        }}
                                        variant="outlined"
                                    />
                                </FormControl>
                            ) : (
                                <FormControl variant="outlined" className={classes.popupElement}>
                                    <GenericDatePicker
                                        minDate={new Date(startDate.getTime() + 86400000)}
                                        minMsg={t("AheadOfStartTime")}
                                        dateValue={endDate}
                                        onDateChange={onEndDateChangeHandler}
                                        label={t("EndDate")}
                                    />
                                </FormControl>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <Grid item xs={12} style={{ marginTop: 0 }}>
                <Button style={{ margin: 20, float: "right" }} onClick={submit} variant="contained" color="primary">
                    {t("Complete")}
                </Button>
                <Button style={{ margin: 20, float: "right" }} onClick={showDays} variant="contained" color="secondary">
                    {t("ShowDates")}
                </Button>
            </Grid>
        </div>
    );
};

export default RuleGenerator;
